import { ArchiveAnalysisForm } from '@modules/analysis/components/ArchiveAnalysisForm';
import { ChangeAnalysisOwnerForm } from '@modules/analysis/components/ChangeAnalysisOwnerForm';
import { isSavedAnalysis } from '@modules/analysis/helpers';
import { isAnalysisAuthor } from '@modules/analysis/helpers/isAnalysisAuthor';
import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { useIsAuthorized } from '@modules/common/components/CanAccess/hooks';
import { MenuItemOptions } from '@modules/common/components/KebabMenu/types';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import {
  EXPORT_CHART_AS_IMAGE_WIDTH,
  NAV_PATHS,
} from '@modules/common/constants';
import { useExportChartAsImage } from '@modules/common/hooks/useExportChartAsImage';
import {
  useExportQuantifyReportDataAsCSV,
  useIsSingleRecordQuery,
} from '@modules/quantify/hooks';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import {
  getQuantifyAnalysisURL,
  isAggregateFormula,
  isValidQuery,
} from '@modules/quantify/utils';
import { THEME_MODE_QUERY_PARAM } from '@modules/theme/constants';
import { useThemeStore } from '@modules/theme/store/useThemeStore';
import {
  CalendarViewMonthRounded,
  ChatRounded,
  ControlPointDuplicateRounded,
  DownloadRounded,
  GroupRounded,
  Inventory2Rounded,
  SimCardDownloadRounded,
} from '@mui/icons-material';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import { PermissionOperation } from 'generated/graphql';
import { compressToEncodedURIComponent } from 'lz-string';
import { useCallback, useMemo } from 'react';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { useFeatureFlag } from 'v2/core/clients/featureFlag';
import { changeAnalysisOwnerFFlag } from 'v2/core/clients/featureFlag/flags';
import { SuperPermissionResource } from 'v2/core/entities/user';
import { mapFromEdgeScope } from 'v2/core/repository/edge-server/analysis/fromEdgeAnalysis';
import { shallow } from 'zustand/shallow';
import { useChangeQuantifyScope } from './useChangeQuantifyScope';
import { useDuplicateQuantify } from './useDuplicateQuantify';
import { useExportQuantifyFeedback } from './useExportQuantifyFeedback';

const trackQuantifyReportEvents = createAnalyticsTracker('quantifyReport');
const trackQuantifyExportImageEvents = createAnalyticsTracker(
  'quantifyExportChartAsImage',
);

export const useGetQuantifyMoreActions = () => {
  const { mode } = useThemeStore(({ mode }) => ({ mode }));
  const analysis = useAnalysisStore((state) => state);
  const { id, isEditable } = analysis;

  const { openModal } = useMuiModalContext();
  const handleDuplicateQuantify = useDuplicateQuantify();
  const handleChangeQuantifyScope = useChangeQuantifyScope();
  const [, changeOwnerEnabled] = useFeatureFlag(changeAnalysisOwnerFFlag);

  const openExportFeedbackRecordsModal = useExportQuantifyFeedback();

  const {
    whereClause,
    duration,
    groupBy,
    compareTo,
    metadata,
    formulaID,
    measuredAsCriteria,
    denominatorWhereClause,
  } = useQuantifyStore(
    ({ query, metadata }) => ({
      whereClause: query.whereClauseConfig?.whereClause,
      duration: query.duration,
      groupBy: query.groupBy,
      compareTo: query.compareTo,
      formulaID: query.formulaID,
      measuredAsCriteria: query.measuredAsCriteria,
      denominatorWhereClause: query.denominatorWhereClause,
      metadata,
    }),
    shallow,
  );

  const queryMetadata = JSON.stringify(metadata);

  const searchParams = getQuantifyAnalysisURL({
    groupByValue: groupBy?.field,
    duration: duration,
    whereClauseConfig: {
      whereClause: whereClause ?? null,
    },
    compareTo,
    formulaID,
    measuredAsCriteriaValue: measuredAsCriteria?.field,
    denominatorWhereClause: denominatorWhereClause,
    ...metadata,
  });

  const themeURLParam = `${THEME_MODE_QUERY_PARAM}=${mode}`;

  const quantifyURLSlugForQuery = compressToEncodedURIComponent(
    `${searchParams}${
      analysis.id ? `&analysisName=${encodeURIComponent(analysis.title)}` : ''
    }&${themeURLParam}`,
  );

  const { exportChartAsImage: exportChartAsImageAPI } = useExportChartAsImage({
    chartTitle: analysis.title,
    chartURLSlug: `report/${NAV_PATHS.QUANTIFY}?${quantifyURLSlugForQuery}`,
    imageSettings: {
      width: EXPORT_CHART_AS_IMAGE_WIDTH,
    },
  });

  const exportChartAsImage = useCallback(() => {
    trackQuantifyExportImageEvents({
      eventName: 'Clicked Export As Image',
      chartType: metadata.activeVisualisationType,
      isSavedAnalysis: !!id,
    });
    exportChartAsImageAPI();
  }, [exportChartAsImageAPI, id, metadata.activeVisualisationType]);

  const openArchiveModal = useCallback(
    () =>
      openModal({
        contentComponent: () => (
          <ArchiveAnalysisForm
            queryMetadata={queryMetadata}
            analysis={analysis}
          />
        ),
        modalProps: {
          fullWidth: true,
          maxWidth: 'sm',
          PaperProps: {
            elevation: 3,
          },
        },
      }),
    [openModal, queryMetadata, analysis],
  );

  const openChangeOwnerModal = useCallback(
    () =>
      openModal({
        contentComponent: () => <ChangeAnalysisOwnerForm analysis={analysis} />,
        modalProps: {
          fullWidth: true,
          maxWidth: 'sm',
          PaperProps: {
            elevation: 3,
          },
        },
      }),
    [analysis, openModal],
  );

  const [exportAsCSV] = useExportQuantifyReportDataAsCSV();

  const handleExportTableDataAsCSV = useCallback(() => {
    exportAsCSV();
    trackQuantifyReportEvents({
      eventName: 'Quantify Report - Download CSV',
      name: analysis.title,
      link: analysis.href,
      scope: mapFromEdgeScope(analysis.scope),
    });
  }, [analysis.href, analysis.scope, analysis.title, exportAsCSV]);

  const isOwner = isAnalysisAuthor({
    analysisID: id,
    isEditable,
  });
  const canAccessDiscoverability = useIsAuthorized({
    resource: SuperPermissionResource.Discoverability,
    operation: PermissionOperation.Write,
  });
  const isSingleRecordQuery = useIsSingleRecordQuery();
  const shouldShowDuplicateQuantify = isSavedAnalysis(id) && isOwner;
  const shouldShowChangeQuantifyOwner = isOwner && changeOwnerEnabled;

  const shouldShowExportRecords = useQuantifyStore(({ reportData, query }) => {
    if (!isValidQuery(query)) {
      return false;
    }

    if (isAggregateFormula(query.formulaID) && isSingleRecordQuery) {
      return !!(query.compareTo
        ? reportData?.compare.aggregateFormulaResponse
        : reportData?.quantify.aggregateFormulaResponse);
    }

    return !!(query.compareTo
      ? reportData?.compare.tableRows
      : reportData?.quantify.tableRows);
  });

  const moreActions: MenuItemOptions = useMemo(() => {
    const exportMenuSubItems = [
      {
        icon: <ChatRounded fontSize="small" />,
        label: 'Export Feedback',
        onClick: openExportFeedbackRecordsModal,
      },
      {
        icon: <CalendarViewMonthRounded fontSize="small" />,
        label: 'Export Table as CSV',
        onClick: handleExportTableDataAsCSV,
      },
      {
        icon: <SimCardDownloadRounded fontSize="small" />,
        label: 'Export Chart as Image',
        onClick: exportChartAsImage,
      },
    ];

    return [
      ...(isOwner
        ? [
            {
              icon: <Inventory2Rounded fontSize="small" />,
              label: 'Archive',
              onClick: openArchiveModal,
            },
          ]
        : []),
      ...(shouldShowExportRecords
        ? [
            {
              icon: <DownloadRounded fontSize="small" />,
              label: 'Export Quantify as',
              subItems: exportMenuSubItems,
            },
          ]
        : []),
      ...(isOwner && canAccessDiscoverability
        ? [
            {
              icon: <TravelExploreRoundedIcon fontSize="small" />,
              label: 'Change Discoverability',
              onClick: handleChangeQuantifyScope,
            },
          ]
        : []),
      ...(shouldShowChangeQuantifyOwner
        ? [
            {
              icon: <GroupRounded fontSize="small" />,
              label: 'Change Owner',
              onClick: openChangeOwnerModal,
            },
          ]
        : []),
      ...(shouldShowDuplicateQuantify
        ? [
            {
              icon: <ControlPointDuplicateRounded fontSize="small" />,
              label: 'Duplicate',
              onClick: handleDuplicateQuantify,
            },
          ]
        : []),
    ];
  }, [
    openExportFeedbackRecordsModal,
    handleExportTableDataAsCSV,
    isOwner,
    openArchiveModal,
    canAccessDiscoverability,
    handleChangeQuantifyScope,
    shouldShowChangeQuantifyOwner,
    openChangeOwnerModal,
    shouldShowExportRecords,
    shouldShowDuplicateQuantify,
    handleDuplicateQuantify,
    exportChartAsImage,
  ]);

  return moreActions;
};
